export interface QuestionPerformancePayload {
    schoolCode: string;
    familyCode: string;
    login: string;
    dicCode: string;
    lsnTestCode: string;
    sessionId: number;
    lsnCode?: string;
    qnNo: string;
    subNo: number;
    qnType: string;
    marks: number;
    noOfOptions: number;
    optOrder?: string;
    /** Legacy alias for EL_LsnReportDetails.OptOrder. */
    OptOrder?: string;
    enteredCorrectAnswer: string;
    enteredWrongAnswer: string;
    enteredBlankAnswer: string;
    solutionSeen: string | number;
    clueSeen: boolean | string;
    principleSeen: boolean | string;
    questionLoadTime: number;
    questionAnswerTime: number;
    lastQnInd: number;
    varValues?: string;
    scoringMethod?: number | null;
    allQuestionsCompleted?: string;
    deferCompletionStatus?: boolean;
    chapterCode?: string;
    unitCode?: string;
    hwid?: number | null;
    testType?: 'K' | 'A';
    subject?: string;
    /** Legacy format: LastQnInd QnNo SubNo QnType. */
    nextQnData?: string;
    /** Legacy EL_LsnReport.ShuffledQnsOrder format. */
    dbgShuffledQnsOrder?: string;
    /** Legacy EL_LsnReport.hidQuestionData snapshot. */
    hiddenQnData?: string;
    HiddenQnData?: string;
    hidQuestionData?: string;
    completionTotals?: CompletionTotals;
}

interface CompletionTotals {
    totalQuestions: number;
    correctAnswers: number;
    incorrectAnswers: number;
    totalLoadTime: number;
    totalAnswerTime: number;
    fullSolutionViewed: number;
    partialSolutionViewed: number;
}

const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;
const inFlightPerformanceRequests = new Map<string, Promise<any>>();
const savedQuestionPayloads = new Map<string, Map<string, QuestionPerformancePayload>>();

const getPerformanceRequestKey = (payload: QuestionPerformancePayload): string =>
    [
        payload.schoolCode,
        payload.familyCode,
        payload.login,
        payload.dicCode,
        payload.lsnTestCode,
        payload.sessionId,
        payload.lsnCode || '',
        payload.qnNo,
        payload.subNo,
    ].join('|');

const getSessionKey = (payload: QuestionPerformancePayload): string =>
    [
        payload.schoolCode,
        payload.familyCode,
        payload.login,
        payload.dicCode,
        payload.lsnTestCode,
        payload.sessionId,
    ].join('|');

const isCompletionRequest = (payload: QuestionPerformancePayload): boolean =>
    String(payload.allQuestionsCompleted).toLowerCase() === 'true';

export const buildNextQuestionData = (questions: any[] | undefined, currentPage: number): string => {
    const nextQuestion = questions?.[currentPage];
    if (!nextQuestion) return '';

    return [
        nextQuestion.LastQnInd ?? '',
        nextQuestion.QnNo ?? '',
        nextQuestion.SubNo ?? 0,
        nextQuestion.QnType ?? '',
    ].join(' ');
};

export const buildShuffledQuestionsOrder = (questions: any[] | undefined): string =>
    (questions || [])
        .map(question => `${question.QnNo ?? ''}^${question.SubNo ?? 0}*${question.LsnCode ?? '0'}`)
        .join('|');

const readHiddenQuestionData = (): string => {
    const hiddenQuestionDataInput = document.getElementById('hidQuestionData') as HTMLInputElement | null;
    return hiddenQuestionDataInput?.value ?? '';
};

const extractOrderToken = (value: string): string => {
    const trimmed = (value || '').trim();
    if (!trimmed) return '';

    if (/^\d+$/.test(trimmed)) return trimmed;

    const match = trimmed.match(/(\d+)/);
    if (match) return match[1];

    const alphaToken = trimmed.replace(/[^A-Za-z]/g, '');
    if (alphaToken) return alphaToken;

    const digitsOnly = trimmed.replace(/\D+/g, '');
    return digitsOnly || trimmed;
};

const getOptionOrderValue = (row: HTMLElement): string => {
    const rowName = row.getAttribute('name') || '';
    const rowId = row.getAttribute('id') || '';
    const radio = row.querySelector<HTMLInputElement>('input[id^="KSD_Rad_"]')
        ?? row.querySelector<HTMLInputElement>('input[type="radio"]')
        ?? row.querySelector<HTMLInputElement>('input[name]');

    const fromRadio = radio?.getAttribute('name') || radio?.name || '';
    const fromRadioName = extractOrderToken(fromRadio);

    // Important: the legacy DB stores the displayed row order, not the static
    // original row metadata.  If the DOM has been shuffled, the actual row
    // sequence in the table must win over stale row ids/name attributes.
    if (fromRadioName) return fromRadioName;

    const fromRowId = extractOrderToken(rowId);
    const fromRowName = extractOrderToken(rowName);
    return fromRowName || fromRowId || '';
};

export const getLegacyQuestionOptionRows = (document: Document): HTMLTableRowElement[] => {
    const allRows = Array.from(
        document.querySelectorAll<HTMLTableRowElement>('table[id^="KSD_TABLE"] tbody tr, #KSD_TABLE tbody tr, tr[id^="KSD_TR_"] , tr[id^="TR_"]')
    );

    if (allRows.length === 0) return [];

    const legacyRows = allRows.filter((row) => {
        const hasLegacyId = row.id.startsWith('KSD_TR_') || row.id.startsWith('TR_');
        const hasLegacyName = !!row.getAttribute('name');
        const hasLegacyCell = !!row.querySelector('td[id="KSD_TD1"], td[id="KSD_TD2"], td[id^="KSD_TD_"]');
        const hasLegacyInput = !!row.querySelector('input[id^="KSD_Rad_"], input[id^="Chk_"], input[type="radio"], input[type="checkbox"], input[name]');
        return hasLegacyId || hasLegacyName || hasLegacyCell || hasLegacyInput;
    });

    return legacyRows.length > 0 ? legacyRows : allRows;
};

const getDocumentRadioOrder = (document: Document): string[] => {
    const radios = Array.from(
        document.querySelectorAll<HTMLInputElement>('input[type="radio"], input[id^="KSD_Rad_"], input[name]')
    );

    return radios
        .map((radio) => {
            const name = radio.getAttribute('name') || radio.name || '';
            return extractOrderToken(name) || extractOrderToken(radio.id || '');
        })
        .filter(Boolean);
};

export const swapMatchingOptionCells = (
    document: Document,
    firstCellId: string,
    secondCellId: string,
): boolean => {
    const firstCell = document.getElementById(firstCellId);
    const secondCell = document.getElementById(secondCellId);

    if (!firstCell || !secondCell) {
        return false;
    }

    const firstHtml = firstCell.innerHTML;
    const secondHtml = secondCell.innerHTML;
    const firstId = firstCell.id;
    const secondId = secondCell.id;
    const firstName = firstCell.getAttribute('name');
    const secondName = secondCell.getAttribute('name');

    firstCell.innerHTML = secondHtml;
    secondCell.innerHTML = firstHtml;
    firstCell.id = secondId;
    secondCell.id = firstId;

    if (firstName !== null) firstCell.setAttribute('name', secondName ?? firstName);
    if (secondName !== null) secondCell.setAttribute('name', firstName ?? secondName);

    return true;
};

const resolveHiddenQuestionData = (payload: QuestionPerformancePayload): string => {
    const candidate = (
        payload.hiddenQnData
        ?? payload.HiddenQnData
        ?? payload.hidQuestionData
        ?? ''
    ) as string | undefined;

    const trimmedCandidate = (candidate ?? '').toString().trim();
    return trimmedCandidate || readHiddenQuestionData() || '';
};

const normalizeLegacyPayload = (payload: QuestionPerformancePayload): QuestionPerformancePayload => {
    const normalizedOptOrder = payload.optOrder ?? payload.OptOrder ?? '';
    const normalizedHiddenQnData = resolveHiddenQuestionData(payload);

    return {
        ...payload,
        optOrder: normalizedOptOrder,
        OptOrder: normalizedOptOrder,
        hiddenQnData: normalizedHiddenQnData,
        HiddenQnData: normalizedHiddenQnData,
        hidQuestionData: normalizedHiddenQnData,
    };
};

/**
 * Matches the legacy EL_*.js ShuffledOptionsOrder values from the question
 * iframe.  Only question types that shuffle options return a value; all
 * others remain empty and therefore are not written as an option order.
 */
export const buildOptionOrder = (
    iframe: HTMLIFrameElement | null,
    questionType: 'O' | 'C' | 'D' | 'F' | 'M' | 'L',
): string => {
    console.log('%c[OPTORDER-BUILD-CHECK] v2 running', 'color:red;font-weight:bold');
    const document = iframe?.contentDocument;
    if (!document) return '';

    const rows = getLegacyQuestionOptionRows(document).filter((row) => {
        const hasLegacyOptionControl = !!row.querySelector('input[id^="KSD_Rad_"]')
            || !!row.querySelector('input[type="radio"]')
            || !!row.querySelector('input[name]')
            || !!row.querySelector('input[id^="Chk_"]')
            || !!row.querySelector('input[type="checkbox"]')
            || row.id.startsWith('KSD_TR_')
            || row.id.startsWith('TR_');
        return hasLegacyOptionControl;
    });

    // if (questionType === 'D') {
    //     return Array.from(document.querySelectorAll<HTMLSelectElement>('select#KSD_SELECT'))
    //         .map((select, dropdownIndex) => {
    //             const optionOrder = Array.from(select.options)
    //                 .map((_, optionIndex) => String(optionIndex).padStart(2, '0'))
    //                 .join('');
    //             return `${dropdownIndex}-${optionOrder}`;
    //         })
    //         .filter((value) => !value.endsWith('-'))
    //         .join(',');
    // }

    if (questionType === 'D') {
        const selects = Array.from(
            document.querySelectorAll<HTMLSelectElement>(
                'select[id^="dropdown_"], select#KSD_SELECT'
            )
        );

        const optOrder = selects
            .map((select, dropdownIndex) => {
                // Ignore the React-added "-- Select --" option.
                const realOptions = Array.from(select.options).filter(
                    option =>
                        option.value !== '' &&
                        option.text.trim() !== '-- Select --'
                );

                if (realOptions.length === 0) {
                    return '';
                }

                const optionOrder = realOptions
                    .map((_, optionIndex) =>
                        String(optionIndex).padStart(2, '0')
                    )
                    .join('');

                return `${dropdownIndex}-${optionOrder}`;
            })
            .filter(Boolean)
            .join(',');

        console.log('[buildOptionOrder] D type:', {
            selectCount: selects.length,
            optOrder,
            selectIds: selects.map(select => select.id),
        });

        return optOrder;
    }

    if (questionType === 'F') {
        return Array.from(document.querySelectorAll<HTMLElement>(
            'td[id^="KSD_FIGTD_"] > span[id^="KSD_WLIST_DragDrop_"]',
        ))
            .map((_, optionIndex) => String(optionIndex))
            .join(',');
    }

    // if (questionType === 'M') {
    //     const leftOrder = rows
    //         .map(row => row.querySelector<HTMLElement>('td[id^="LHS2_"]')?.id.slice(5) || '')
    //         .filter(Boolean)
    //         .join(',');
    //     const rightOrder = rows
    //         .map(row => row.querySelector<HTMLElement>('td[id^="RHS2_"]')?.id.slice(5) || '')
    //         .filter(Boolean)
    //         .join(',');
    //     return leftOrder && rightOrder ? `${leftOrder}~${rightOrder}` : '';
    // }

    if (questionType === 'M') {
        const matchingRows = Array.from(
            document.querySelectorAll<HTMLTableRowElement>(
                'table[id^="KSD_TABLE"] tr'
            )
        ).filter(row =>
            row.querySelector('td[id^="LHS2_"]') &&
            row.querySelector('td[id^="RHS2_"]')
        );

        const leftOrder = matchingRows
            .map(row =>
                row.querySelector<HTMLElement>('td[id^="LHS2_"]')
                    ?.id
                    .replace(/^LHS2_/, '') || ''
            )
            .filter(Boolean)
            .join(',');

        const rightOrder = matchingRows
            .map(row =>
                row.querySelector<HTMLElement>('td[id^="RHS2_"]')
                    ?.id
                    .replace(/^RHS2_/, '') || ''
            )
            .filter(Boolean)
            .join(',');

        const optOrder =
            leftOrder && rightOrder
                ? `${leftOrder}~${rightOrder}`
                : '';

        console.log('[buildOptionOrder] M type:', {
            rowCount: matchingRows.length,
            leftOrder,
            rightOrder,
            optOrder,
        });

        return optOrder;
    }

    if (questionType === 'L') {
        return rows
            .filter(row => row.id.startsWith('TR_'))
            .map(row => row.id.slice(3))
            .filter(Boolean)
            .join('~');
    }

    const optionRows = rows.filter(row => {
        const hasLegacyId = row.id.startsWith('KSD_TR_') || row.id.startsWith('TR_');
        const hasLegacyName = !!row.getAttribute('name') || !!row.querySelector('input[name]');
        return hasLegacyId || hasLegacyName;
    });
    const fallbackRows = optionRows.length > 0 ? optionRows : rows;

    // Try the row-scoped extraction FIRST. It's the type-aware, defensive
    // path (row id -> row name -> radio name), and it's confined to rows we
    // actually identified as option rows. Previously the document-wide radio
    // scan below ran first and short-circuited whenever it found ANY radio
    // or named <input> anywhere in the iframe (including unrelated hidden
    // fields like KSD_Ans / KSD_MW_Info / hidBlobAssetsPath), which both
    // starved this branch of ever running and could pull in garbage tokens
    // from elements that have nothing to do with the option order.
    if (fallbackRows.length > 0) {
        const rowOrder = fallbackRows
            .map(row => {
                if (questionType === 'O') return getOptionOrderValue(row);
                // EL_C stores the row IDs in the order the rows are currently
                // displayed. We intentionally do not prefer the static
                // original row metadata when the table has already been
                // shuffled.
                const rowOrderValue = getOptionOrderValue(row);
                const orderToken = extractOrderToken(row.id);
                return rowOrderValue || orderToken;
            })
            .filter(Boolean)
            .join('~');

        if (rowOrder) return rowOrder;

        console.warn('[buildOptionOrder] row-scoped extraction produced no tokens', {
            questionType,
            rowCount: fallbackRows.length,
            rowIds: fallbackRows.map(row => row.id),
        });
    } else {
        console.warn('[buildOptionOrder] no option rows found for question type', questionType);
    }

    // Last resort only: scan the whole iframe document for radio-like inputs.
    const directRadioOrder = getDocumentRadioOrder(document);
    if (directRadioOrder.length > 0) {
        console.warn('[buildOptionOrder] falling back to document-wide radio scan', directRadioOrder);
        return directRadioOrder.join('~');
    }

    return '';
};

const calculateCompletionTotals = (
    questions: Iterable<QuestionPerformancePayload>,
): CompletionTotals => {
    const values = Array.from(questions);
    const correctAnswers = values.filter(
        question => question.marks !== 0 && question.enteredCorrectAnswer !== null,
    ).length;
    const solutionStates = values.map(question => String(question.solutionSeen ?? '').trim());

    return {
        totalQuestions: values.length,
        correctAnswers,
        incorrectAnswers: values.length - correctAnswers,
        totalLoadTime: values.reduce((total, question) => total + (Number(question.questionLoadTime) || 0), 0),
        totalAnswerTime: values.reduce((total, question) => total + (Number(question.questionAnswerTime) || 0), 0),
        fullSolutionViewed: solutionStates.filter(state => state === 'Y').length,
        partialSolutionViewed: solutionStates.filter(
            state => state !== '' && state !== 'N' && state !== 'Y',
        ).length,
    };
};


export const saveQuestionPerformance = async (payload: QuestionPerformancePayload): Promise<any> => {
    const params = new URLSearchParams(window.location.search);
    const isViewLesson = params.get("ViewLesson") === "Y";

    // Preview mode - do NOT update the database
    if (isViewLesson) {
        return {
            success: true,
            message: "Skipped update_question_performance in View Lesson."
        };
    }

    const requestKey = getPerformanceRequestKey(payload);
    const existingRequest = inFlightPerformanceRequests.get(requestKey);
    if (existingRequest) return existingRequest;

    const sessionKey = getSessionKey(payload);
    const savedQuestions = savedQuestionPayloads.get(sessionKey) || new Map<string, QuestionPerformancePayload>();
    savedQuestions.set(requestKey, payload);
    console.log("[QUESTION SAVE DEBUG]", {
        qnNo: payload.qnNo,
        subNo: payload.subNo,
        lsnCode: payload.lsnCode,
        requestKey
    });
    savedQuestionPayloads.set(sessionKey, savedQuestions);

    let completionSucceeded = false;
    const requestPromise = (async () => {
        try {
            const accessToken = localStorage.getItem('access_token');

            if (!accessToken) {
            }

            if (isCompletionRequest(payload)) {
                // Normal answer saves are independent. The final request waits for all
                // outstanding saves, then sends the accumulated totals once.
                await Promise.all(
                    Array.from(inFlightPerformanceRequests.entries())
                        .filter(([key]) => key !== requestKey && key.startsWith(`${sessionKey}|`))
                        .map(([, request]) => request.catch(() => undefined)),
                );
            }

            const apiPayload: QuestionPerformancePayload = normalizeLegacyPayload({
                ...payload,
                solutionSeen: typeof payload.solutionSeen === 'boolean' ? (payload.solutionSeen ? 'Y' : 'N') : payload.solutionSeen,
                clueSeen: typeof payload.clueSeen === 'boolean' ? (payload.clueSeen ? 'Y' : 'N') : payload.clueSeen,
                principleSeen: typeof payload.principleSeen === 'boolean' ? (payload.principleSeen ? 'Y' : 'N') : payload.principleSeen,
                deferCompletionStatus:
                    isCompletionRequest(payload) &&
                    [
                        '/student/tutorials/question',
                        '/student/tutorialscopy/question',
                    ].includes(
                        window.location.pathname.toLowerCase().replace(/\/+$/, ''),
                    ),
            });

            if (isCompletionRequest(payload)) {
                apiPayload.completionTotals = calculateCompletionTotals(savedQuestions.values());
                console.info('[update_question_performance] completion totals', apiPayload.completionTotals);
            }

            console.log('[update_question_performance] payload-debug', {
                qnNo: apiPayload.qnNo,
                qnType: apiPayload.qnType,
                optOrder: apiPayload.optOrder,
                OptOrder: apiPayload.OptOrder,
                hiddenQnData: apiPayload.hiddenQnData,
                HiddenQnData: apiPayload.HiddenQnData,
                hidQuestionData: apiPayload.hidQuestionData,
                rawPayload: apiPayload,
            });

            const traceQuery = isCompletionRequest(payload) ? '?performanceTrace=1' : '';
            const response = await fetch(`${apiUrl}update_question_performance/${traceQuery}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify(apiPayload),
            });

            if (!response.ok) {
                window.location.replace("/timeout");

                console.error("API Error Response:V1`", response);

                let errorData;
                try {
                    errorData = await response.json();
                    console.error("API Error Response:V2", errorData);
                } catch (e) {
                    console.error("API Error Response:V3", e);
                    errorData = { detail: `HTTP error ${response.status}: ${response.statusText}` };
                }
                console.error("API Error Response: V4", errorData);
                throw new Error(errorData.detail || errorData.message || 'Failed to save question performance.');
            }

            const result = await response.json();
            completionSucceeded = isCompletionRequest(payload);
            if (completionSucceeded && result.performanceMs) {
                console.info('[update_question_performance] completion timings (ms)', result.performanceMs);
            }
            const isLessonQuestionPage = window.location.pathname
                .toLowerCase()
                .replace(/\/+$/, '');
            if (
                completionSucceeded &&
                [
                    '/student/tutorials/question',
                    '/student/tutorialscopy/question',
                ].includes(isLessonQuestionPage) &&
                payload.dicCode &&
                payload.lsnTestCode &&
                payload.sessionId
            ) {
                localStorage.setItem(
                    `completedLesson:${payload.dicCode}:${payload.lsnTestCode}`,
                    String(payload.sessionId),
                );
            }
            return result;
        } catch (error) {
            console.error('Error saving question performance:', error);
            throw error;
        }
    })();

    inFlightPerformanceRequests.set(requestKey, requestPromise);
    const clearRequestState = () => {
        inFlightPerformanceRequests.delete(requestKey);
        if (completionSucceeded) {
            savedQuestionPayloads.delete(sessionKey);
        }
    };
    void requestPromise.then(clearRequestState, clearRequestState);

    return requestPromise;
};
